<script setup lang="ts" generic="T extends { id: string | number; children: unknown[] }">
import { onMounted, useTemplateRef } from "vue";

import DropdownMenu from "@/components/ui/DropdownMenu/DropdownMenu.vue";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import { useNativeEvents } from "@/composables/useNativeEvents";

import { useHoverMenu } from "./internal/useHoverMenu";

defineProps<{ header?: string; items: T[]; modelValue: T[] }>();

const emit = defineEmits<{ "update:modelValue": [value: T[]] }>();

const { addEventListener } = useNativeEvents();
const { activeId, handleHoverEnter, handleHoverLeave } = useHoverMenu();
const rootRef = useTemplateRef<HTMLElement>("rootRef");

// Handle selection changes from SelectableList
function handleSelectionChange(selected: T[]) {
  emit("update:modelValue", selected);
}

// Helper to extract item ID from event target
function getItemIdFromEvent(event: PointerEvent): string | null {
  const wrapper = (event.target as HTMLElement).closest(".menu-list-item .wrapper");
  return (
    wrapper?.querySelector("[data-cascade-item-id]")?.getAttribute("data-cascade-item-id") ?? null
  );
}

// Attach hover event listeners using event delegation
onMounted(() => {
  const component = rootRef.value;
  if (!component) return;

  addEventListener(
    component,
    "pointerover",
    (event) => {
      const id = getItemIdFromEvent(event);
      if (id) handleHoverEnter(id);
    },
    { capture: true, passive: true },
  );

  addEventListener(
    component,
    "pointerout",
    (event) => {
      const id = getItemIdFromEvent(event);
      if (id) handleHoverLeave(id);
    },
    { capture: true, passive: true },
  );
});
</script>

<template>
  <div ref="rootRef" class="cascading-select-list">
    <SelectableList
      :model-value
      :values="items"
      :header
      @update:model-value="handleSelectionChange"
    >
      <template #default="{ item }">
        <DropdownMenu
          placement="right-start"
          :offset="{ left: -14, top: 9 }"
          stay-open-on-content-click
          width="sm"
          :is-open="activeId === String(item.id)"
          :data-cascade-item-id="String(item.id)"
        >
          <template #trigger>
            <div class="cascade-trigger">
              <slot name="trigger" :item="item" />
            </div>
          </template>

          <div
            @pointerenter="handleHoverEnter(String(item.id))"
            @pointerleave="handleHoverLeave(String(item.id))"
          >
            <slot name="submenu" :item="item" :children="item.children" />
          </div>
        </DropdownMenu>
      </template>
    </SelectableList>
  </div>
</template>

<style scoped>
:deep(.menu-list-item .wrapper) {
  padding: 0;
}

:deep(.menu-list-item .dropdown-menu) {
  display: block;
  padding: 6px;
}

.cascade-trigger {
  display: flex;
  gap: 16px;
  align-items: center;
  min-width: 180px;
}
</style>
